import { JobApplication } from '../models/job-application.model';
import { JobApplicationService } from '../services/job-application.service';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Container, TableConfigure } from '../../../../../core/components';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  DownloadOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  TrophyOutlined,
  WhatsAppOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { ITableAction, ITableColumnType } from '../../../../../core/interfaces';
import { Button, Popconfirm, Progress, Table, Tag, Tooltip } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import {
  FilterValue,
  SorterResult,
  TableRowSelection,
} from 'antd/es/table/interface';
import { route } from '../../../../../constants/routes';
import {
  applicationScoreBand,
  formatApplicationSkillScore,
} from '../utils/application-relevance-labels';
import JobApplicationViewToggle, {
  JobApplicationViewMode,
  readJobApplicationsViewMode,
} from './JobApplicationViewToggle';
import JobApplicationPipelineBoard from './JobApplicationPipelineBoard';
import { buildPipelineColumns } from '../utils/pipeline-board.utils';
import { JobService } from '../../job/services/job.service';
import { StagesService } from '../../company/services/stages.service';
import { StageId } from '../../company/models/stages.model';
import { useProfile } from '../../profile/components/ProfileContext';
import { UtilService } from '../../../../../core/services/util.service';
import './JobApplicationByJob.css';

const BOARD_PAGE_SIZE = 100;

const getInitials = (name?: string) =>
  (name || 'Candidate')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

const formatAppliedDate = (value?: string) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.split('T')[0];
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const statusTone = (status?: string) => {
  const normalized = status?.toLowerCase() ?? '';
  if (normalized.includes('reject') || normalized.includes('declin'))
    return 'error';
  if (normalized.includes('interview')) return 'purple';
  if (normalized.includes('offer') || normalized.includes('hire'))
    return 'success';
  if (normalized.includes('progress') || normalized.includes('review'))
    return 'processing';
  return 'blue';
};

const columns: ITableColumnType<JobApplication>[] = [
  {
    title: 'Candidate',
    dataIndex: 'candidate',
    key: 'candidate',
    filter: true,
    sorter: true,
    render: (candidate: string) => (
      <div className="job-applications-by-job__candidate">
        <span>{getInitials(candidate)}</span>
        <div>
          <strong>{candidate || 'Unnamed candidate'}</strong>
          <small>Candidate profile</small>
        </div>
      </div>
    ),
  },
  {
    title: 'Contact',
    key: 'contact',
    align: 'center',
    render: (jobApplication: JobApplication) => (
      <div className="job-applications-by-job__contact">
        <Tooltip
          title={jobApplication.email ? 'Send email' : 'Email unavailable'}
        >
          <Button
            type="text"
            icon={<MailOutlined />}
            href={
              jobApplication.email
                ? `mailto:${jobApplication.email}`
                : undefined
            }
            target="_blank"
            rel="noopener noreferrer"
            disabled={!jobApplication.email}
            aria-label="Send email"
          />
        </Tooltip>
        <Tooltip
          title={
            jobApplication.phoneNumber ? 'Open WhatsApp' : 'Phone unavailable'
          }
        >
          <Button
            type="text"
            icon={<WhatsAppOutlined />}
            href={
              jobApplication.phoneNumber
                ? `https://wa.me/${jobApplication.phoneNumber.replace(/\D/g, '')}`
                : undefined
            }
            target="_blank"
            rel="noopener noreferrer"
            disabled={!jobApplication.phoneNumber}
            aria-label="Open WhatsApp"
          />
        </Tooltip>
      </div>
    ),
  },
  {
    title: 'Applied On',
    key: 'appliedOn',
    dataIndex: 'appliedOn',
    sorter: true,
    render: (appliedOn: string) => (
      <span className="job-applications-by-job__date">
        <CalendarOutlined />
        {formatAppliedDate(appliedOn)}
      </span>
    ),
  },
  {
    title: 'Status',
    key: 'status',
    dataIndex: 'status',
    sorter: true,
    render: (status: string) => (
      <Tag
        className="job-applications-by-job__status"
        color={statusTone(status)}
      >
        {status || 'Received'}
      </Tag>
    ),
  },
  {
    title: 'Skill score',
    key: 'relevance',
    dataIndex: 'relevance',
    render: (relevance: number) => {
      const band = applicationScoreBand(relevance);
      const color =
        band === 'STRONG_MATCH'
          ? '#23824b'
          : band === 'PARTIAL_MATCH'
            ? '#c17a13'
            : '#b54747';
      return (
        <div className="job-applications-by-job__score">
          <div>
            <strong style={{ color }}>
              {formatApplicationSkillScore(relevance)}
            </strong>
          </div>
          <Progress
            percent={Math.max(0, Math.min(100, relevance ?? 0))}
            showInfo={false}
            strokeColor={color}
            trailColor="#e8edf2"
            size="small"
          />
        </div>
      );
    },
  },
];

const JobApplicationByJob: React.FC = () => {
  const [data, setData] = useState<JobApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [sort, setSort] = useState<string>();
  const [filter, setFilter] = useState<string>();
  const { companyId } = useProfile();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const jobName = location.state?.jobName as string | undefined;
  const [viewMode, setViewMode] = useState<JobApplicationViewMode>(
    () => readJobApplicationsViewMode(location.state) ?? 'table'
  );
  const [stages, setStages] = useState<StageId[]>([]);
  const [clientId, setClientId] = useState<string>();
  const [boardLoading, setBoardLoading] = useState(false);
  const [selectedApplicationIds, setSelectedApplicationIds] = useState<
    React.Key[]
  >([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const restored = readJobApplicationsViewMode(location.state);
    if (restored) {
      setViewMode(restored);
    }
  }, [location.key, location.state]);

  const GoBack = () => {
    if (location.state?.fromDashboard) {
      navigate(route('/dashboard'));
    } else {
      navigate(route('/job'));
    }
  };

  const openApplication = useCallback(
    (item: JobApplication) => {
      navigate(route(`/job-applications/${item.id}`), {
        state: {
          jobName: item.jobName || jobName,
          candidateName: item.candidate,
          fromJobApplicationsByJob: true,
          jobId: id,
          jobApplicationsViewMode: viewMode,
          ...(location.state?.fromDashboard ? { fromDashboard: true } : {}),
        },
      });
    },
    [id, jobName, location.state?.fromDashboard, navigate, viewMode]
  );

  const GetApplications = useCallback(async () => {
    if (!id) return;

    const isBoard = viewMode === 'board';
    const params = {
      page: isBoard ? 1 : pagination.current,
      size: isBoard ? BOARD_PAGE_SIZE : pagination.pageSize,
      ...(sort ? { sort } : {}),
      ...(filter ? { filter } : {}),
    };

    if (isBoard) {
      setBoardLoading(true);
    }

    try {
      const result = await JobApplicationService.GetApplicationsById(
        id,
        params
      );
      if (result) {
        setData(result.content.map((item: JobApplication) => ({ ...item })));
        setTotal(result.totalElements);
        setSelectedApplicationIds([]);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      if (isBoard) {
        setBoardLoading(false);
      }
    }
  }, [filter, id, pagination, sort, viewMode]);

  useEffect(() => {
    if (id) {
      void GetApplications();
    }
  }, [GetApplications, id]);

  useEffect(() => {
    const loadStages = async () => {
      if (!companyId || !id || viewMode !== 'board') {
        return;
      }

      try {
        const job = await JobService.GetJob(id);
        const clientIdFromJob = job.clientId || job.client?.id;
        setClientId(clientIdFromJob);
        if (!clientIdFromJob) {
          setStages([]);
          return;
        }

        const result = await StagesService.GetStages(
          companyId,
          clientIdFromJob,
          {
            page: 1,
            size: 100,
            sort: 'statusOrder',
            filter: 'deleted:eq:false',
          }
        );
        setStages(result.content ?? []);
      } catch (error) {
        console.error('Error fetching pipeline stages:', error);
        setStages([]);
      }
    };

    void loadStages();
  }, [companyId, id, viewMode]);

  const pipelineColumns = useMemo(
    () => buildPipelineColumns(stages, data),
    [data, stages]
  );
  const strongMatches = useMemo(
    () =>
      data.filter(
        (application) =>
          applicationScoreBand(application.relevance) === 'STRONG_MATCH'
      ).length,
    [data]
  );

  const onChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<JobApplication> | SorterResult<JobApplication>[]
  ) => {
    const filterParts: string[] = [];
    for (const key in filters) {
      const value = filters[key]?.[0];
      if (value) {
        filterParts.push(`${key}:like:${value}`);
      }
    }
    const filterString =
      filterParts.length > 0 ? filterParts.join(',') : undefined;
    setFilter(filterString);

    if (!Array.isArray(sorter) && sorter.order) {
      const direction = sorter.order === 'ascend' ? '' : '-';
      const sortValue = `${direction}${sorter.field}`;
      setSort(sortValue);
    } else {
      setSort(undefined);
    }

    setPagination({
      current: paginationConfig.current!,
      pageSize: paginationConfig.pageSize!,
    });
  };

  const actions: ITableAction<JobApplication> = {
    visible: true,
    buttons: [
      {
        title: 'View',
        icon: {
          value: <EyeOutlined />,
          size: 'text-lg',
        },
        color: 'blue',
        tone: 500,
        onClick: openApplication,
      },
    ],
    fixed: 'none',
  };

  const downloadExport = (blob: Blob, filename: string) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const exportSelected = async () => {
    if (!id || selectedApplicationIds.length === 0) {
      return;
    }

    try {
      setExporting(true);
      const blob = await JobApplicationService.ExportSelected(
        id,
        selectedApplicationIds.map(String)
      );
      downloadExport(blob, `candidate-export-${id}.zip`);
    } catch (error) {
      console.error('Error exporting candidates:', error);
      UtilService.Alert(
        'Export unavailable',
        'error',
        'The candidate export could not be downloaded. Please try again.'
      );
    } finally {
      setExporting(false);
    }
  };

  const selectAllMatching = async () => {
    if (!id) {
      return;
    }

    try {
      const result = await JobApplicationService.GetApplicationsById(id, {
        page: 1,
        size: 100,
        ...(sort ? { sort } : {}),
        ...(filter ? { filter } : {}),
      });
      setSelectedApplicationIds(
        result.content.map((application) => application.id)
      );
    } catch (error) {
      console.error('Error selecting matching applications:', error);
      UtilService.Alert(
        'Selection unavailable',
        'error',
        'The matching applications could not be selected. Please try again.'
      );
    }
  };

  const rowSelection: TableRowSelection<JobApplication> = {
    selectedRowKeys: selectedApplicationIds,
    onChange: (keys) => setSelectedApplicationIds(keys),
    preserveSelectedRowKeys: true,
  };

  const { columns: enhancedColumns, data: enhancedData } =
    TableConfigure<JobApplication>({
      columns,
      data,
      actions,
    });

  return (
    <Container>
      <div className="job-applications-by-job">
        <header className="job-applications-by-job__header">
          <div>
            <span className="job-applications-by-job__eyebrow">
              HIRING PIPELINE
            </span>
            <h1>{jobName || 'Job Applications'}</h1>
            <p>
              Review candidates, compare fit, and move the strongest applicants
              forward.
            </p>
          </div>
          <Button icon={<ArrowLeftOutlined />} onClick={() => GoBack()}>
            Back to jobs
          </Button>
        </header>

        <div className="job-applications-by-job__metrics">
          <article>
            <span className="job-applications-by-job__metric-icon">
              <TeamOutlined />
            </span>
            <div>
              <small>Total applications</small>
              <strong>{total}</strong>
              <span>Across this job opening</span>
            </div>
          </article>
          <article>
            <span className="job-applications-by-job__metric-icon job-applications-by-job__metric-icon--success">
              <TrophyOutlined />
            </span>
            <div>
              <small>Strong matches on page</small>
              <strong>{strongMatches}</strong>
              <span>Skill score of 75 or higher</span>
            </div>
          </article>
          <article>
            <span className="job-applications-by-job__metric-icon job-applications-by-job__metric-icon--selected">
              <SafetyCertificateOutlined />
            </span>
            <div>
              <small>Selected candidates</small>
              <strong>{selectedApplicationIds.length}</strong>
              <span>Ready for secure export</span>
            </div>
          </article>
        </div>

        <section
          className="ac-applications-workspace job-applications-by-job__workspace"
          aria-label="Job applications views"
        >
          <div className="ac-applications-workspace__toolbar">
            <div className="ac-applications-workspace__controls">
              <JobApplicationViewToggle
                value={viewMode}
                onChange={setViewMode}
              />
              {viewMode === 'table' ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    className="job-applications-by-job__select-all"
                    disabled={total === 0 || exporting}
                    onClick={() => void selectAllMatching()}
                  >
                    Select matching
                  </Button>
                  <Popconfirm
                    title="Download candidate packages?"
                    description="The ZIP contains sensitive candidate information."
                    okText="Download"
                    cancelText="Cancel"
                    onConfirm={() => void exportSelected()}
                    disabled={selectedApplicationIds.length === 0 || exporting}
                  >
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      disabled={selectedApplicationIds.length === 0}
                      loading={exporting}
                    >
                      Download selected ({selectedApplicationIds.length})
                    </Button>
                  </Popconfirm>
                </div>
              ) : null}
            </div>
            {viewMode === 'board' ? (
              <p className="ac-applications-workspace__hint">
                Drag cards between stages, then choose whether to notify the
                candidate.
              </p>
            ) : null}
          </div>

          <div className="ac-applications-workspace__content">
            {viewMode === 'board' ? (
              <JobApplicationPipelineBoard
                applications={data}
                columns={pipelineColumns}
                loading={boardLoading}
                enableDrag
                companyId={companyId ?? undefined}
                clientId={clientId}
                onApplicationClick={openApplication}
                onRefresh={GetApplications}
              />
            ) : (
              <Table<JobApplication>
                className="job-applications-by-job__table"
                size="small"
                columns={enhancedColumns}
                dataSource={enhancedData}
                rowKey="id"
                rowSelection={rowSelection}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total,
                  showSizeChanger: true,
                  onChange: (page, pageSize) =>
                    setPagination({ current: page, pageSize }),
                  onShowSizeChange: (current, size) =>
                    setPagination({ current, pageSize: size }),
                }}
                onChange={onChange}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: (
                    <div className="job-applications-by-job__empty">
                      <TeamOutlined />
                      <strong>No applications yet</strong>
                      <span>
                        New candidates will appear here when they apply.
                      </span>
                    </div>
                  ),
                }}
              />
            )}
          </div>
        </section>
      </div>
    </Container>
  );
};

export default JobApplicationByJob;
